import { ChevronDown } from 'lucide-react'
import {
  createContext,
  useContext,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'
import { cn } from '../lib/cn'

/**
 * Campo — componente 03.
 * Rótulo sempre acima, em caixa alta espaçada — placeholder nunca é rótulo.
 * Obrigatório é o padrão: só o opcional recebe "· opcional"; asterisco não existe.
 * Erro nasce ao sair do campo (`onBlur` na tela), com causa e caminho ("O CPF precisa de 11 dígitos").
 * Máscara visível no vazio (placeholder "dd/mm/aaaa") e `inputMode="numeric"` em CPF, telefone, data e valor.
 */

interface FieldContextValue {
  id: string
  invalid: boolean
  describedBy?: string
}

const FieldContext = createContext<FieldContextValue | null>(null)

function useFieldContext() {
  return useContext(FieldContext)
}

/**
 * O que um controle próprio precisa para se ligar ao `Field` em volta: o `id` que o rótulo aponta e
 * os atributos de erro e de dica. Fora de um `Field`, devolve vazio.
 */
export function useFieldControl(): {
  id?: string
  'aria-invalid'?: true
  'aria-describedby'?: string
} {
  const field = useFieldContext()
  if (!field) return {}
  return {
    id: field.id,
    'aria-invalid': field.invalid || undefined,
    'aria-describedby': field.describedBy,
  }
}

export interface FieldProps {
  label: string
  /** Marca "· opcional" ao lado do rótulo. Obrigatório é o padrão e não recebe marca. */
  optional?: boolean
  /** Mensagem de erro com causa e caminho. Só aparece quando existe. */
  error?: string | null
  /** Ajuda discreta abaixo do campo (ex.: "Sem vírgulas — elas separam procedimentos no documento gerado."). */
  hint?: ReactNode
  /** Complemento do rótulo à direita, como o contador "21/80". */
  meta?: ReactNode
  id?: string
  className?: string
  children: ReactNode
}

export function Field({
  label,
  optional,
  error,
  hint,
  meta,
  id: idProp,
  className,
  children,
}: FieldProps) {
  const generated = useId()
  const id = idProp ?? generated
  const errorId = `${id}-error`
  const hintId = `${id}-hint`
  const invalid = Boolean(error)
  const describedBy =
    [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined

  return (
    <FieldContext.Provider value={{ id, invalid, describedBy }}>
      <div data-slot="field" className={cn('flex flex-col', className)}>
        <div className="flex items-baseline justify-between gap-3">
          <label htmlFor={id} className="eyebrow">
            {label}
            {optional ? (
              <span className="text-label-soft tracking-normal normal-case"> · opcional</span>
            ) : null}
          </label>
          {meta ? (
            <span className="font-body text-eyebrow text-label-soft font-light">{meta}</span>
          ) : null}
        </div>
        <div className="mt-[6px]">{children}</div>
        {error ? (
          <p
            id={errorId}
            role="alert"
            className="font-body text-eyebrow text-plum mt-[6px] leading-[1.5] font-light tracking-normal"
          >
            {error}
          </p>
        ) : null}
        {hint ? (
          <p
            id={hintId}
            className="font-body text-eyebrow text-label mt-[6px] leading-[1.5] font-light tracking-normal"
          >
            {hint}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  )
}

export const inputClassName =
  'block w-full bg-surface border border-hairline-strong px-[14px] py-3 font-body text-[13px] leading-none font-light text-ink outline-none transition-[border-color] placeholder:text-label-soft hover:border-hairline-heavy focus:border-ink focus-visible:outline-none aria-invalid:border-plum [&:not(select)]:read-only:bg-pampas [&:not(select)]:read-only:text-label [&:not(select)]:read-only:hover:border-hairline-strong [&:not(select)]:read-only:focus:border-hairline-strong disabled:bg-pampas disabled:text-label'

export type InputProps = InputHTMLAttributes<HTMLInputElement>

export function Input({ className, id, ...props }: InputProps) {
  const field = useFieldContext()
  return (
    <input
      data-slot="input"
      id={id ?? field?.id}
      aria-invalid={field?.invalid || props['aria-invalid'] || undefined}
      aria-describedby={field?.describedBy}
      className={cn(inputClassName, className)}
      {...props}
    />
  )
}

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>

export function Textarea({ className, id, rows = 3, ...props }: TextareaProps) {
  const field = useFieldContext()
  return (
    <textarea
      data-slot="textarea"
      id={id ?? field?.id}
      rows={rows}
      aria-invalid={field?.invalid || undefined}
      aria-describedby={field?.describedBy}
      className={cn(inputClassName, 'resize-y leading-[1.6]', className)}
      {...props}
    />
  )
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** Texto da opção vazia, ex.: "Selecione". */
  placeholder?: string
}

/** Select nativo restilizado: o azul do sistema operacional não entra. */
export function Select({ className, id, placeholder, children, ...props }: SelectProps) {
  const field = useFieldContext()
  return (
    <div className="relative">
      <select
        data-slot="select"
        id={id ?? field?.id}
        aria-invalid={field?.invalid || undefined}
        aria-describedby={field?.describedBy}
        className={cn(inputClassName, 'cursor-pointer appearance-none pr-10', className)}
        {...props}
      >
        {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
        {children}
      </select>
      <ChevronDown
        aria-hidden
        strokeWidth={1.4}
        className="text-label pointer-events-none absolute top-1/2 right-[14px] size-4 -translate-y-1/2"
      />
    </div>
  )
}

export interface ReadOnlyFieldProps {
  label: string
  value: ReactNode
  /** Sufixo do rótulo; padrão "· não editável". Passe `null` para omitir. */
  note?: string | null
  className?: string
}

/** Dado que a clínica vê mas não edita (CNPJ, e-mail de usuário): fundo pampas e texto rótulo. */
export function ReadOnlyField({
  label,
  value,
  note = '· não editável',
  className,
}: ReadOnlyFieldProps) {
  return (
    <div data-slot="read-only-field" className={cn('flex flex-col', className)}>
      <span className="eyebrow">
        {label}
        {note ? <span className="text-label-soft tracking-normal normal-case"> {note}</span> : null}
      </span>
      <div className="border-hairline bg-pampas font-body text-label mt-[6px] border px-[14px] py-3 text-[13px] leading-none font-light">
        {value}
      </div>
    </div>
  )
}
